/**
 * The answers typed on the /auth/onboard page survive the round trip to
 * Auth0 under the `sessionStorage` key `heidi-onboarding:auth-onboard-answers:v1`, so
 * a denied login can send the user back to a prefilled form. The record is the
 * /auth/onboard page's alone: the in-app flow never resumes from it, it only clears it
 * once the account has saved the onboarding.
 *
 * The key is repeated inline at each storage call below rather than held in a
 * constant, because `scripts/generate-persistence-manifest.ts` records raw Web
 * Storage keys only where it can read them as literals at the call site.
 */

/**
 * `TAnswers` is the caller's form shape: the record is opaque JSON here, and
 * the app names its own form type at each call site.
 */
export const readAuthOnboardAnswers = <
  TAnswers extends object = object,
>(): Partial<TAnswers> | null => {
  if (typeof globalThis.sessionStorage === 'undefined') return null;

  try {
    const serialized = globalThis.sessionStorage.getItem(
      'heidi-onboarding:auth-onboard-answers:v1'
    );
    if (!serialized) return null;

    const parsed: unknown = JSON.parse(serialized);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    return parsed;
  } catch {
    return null;
  }
};

export const writeAuthOnboardAnswers = <TAnswers extends object>(
  answers: TAnswers
): void => {
  if (typeof globalThis.sessionStorage === 'undefined') return;

  try {
    globalThis.sessionStorage.setItem(
      'heidi-onboarding:auth-onboard-answers:v1',
      JSON.stringify(answers)
    );
  } catch {
    // Storage may be unavailable (private mode) or full — the page still
    // submits, it just can't prefill after a denied login.
  }
};

export const clearAuthOnboardAnswers = (): void => {
  if (typeof globalThis.sessionStorage === 'undefined') return;

  try {
    globalThis.sessionStorage.removeItem('heidi-onboarding:auth-onboard-answers:v1');
  } catch {
    // Nothing to recover from — the record expires with the tab regardless.
  }
};
